'use client';

import { useTheme } from 'next-themes';
import { ExternalToast, Toaster as Sonner, toast } from 'sonner';

import { Button, ButtonProps } from '../button';

export type ToasterProps = React.ComponentProps<typeof Sonner>;

export type ToastArgs = ExternalToast & {
  type?: 'success' | 'info' | 'warning' | 'error';
  title: string;
  isAction?: boolean;
  isCancel?: boolean;
  buttonProps?: ButtonProps;
  id: string; // need to specify id to close the toast
};

// Function to create a toast with a custom button
const toastWithCustomButton = ({
  type,
  title,
  id,
  description,
  isAction = false,
  isCancel = false,
  buttonProps = {},
}: ToastArgs) => {
  // Check type of toast function
  const toastFn = type ? toast[type] : toast;

  // Add close toast functionality onClick event
  const handleClickButton = (e: React.MouseEvent<HTMLButtonElement>) => {
    buttonProps.onClick?.(e);
    toast.dismiss(id);
  };

  const customButton = (
    <Button
      variant="soft"
      size="sm"
      style={{
        marginLeft: 'var(--toast-button-margin-start)',
        marginRight: 'var(--toast-button-margin-end)',
        textWrap: 'nowrap',
      }}
      {...buttonProps}
      onClick={handleClickButton}
    >
      {buttonProps.children || 'Close'}
    </Button>
  );

  toastFn(title, {
    id,
    description,
    action: isAction && customButton,
    cancel: isCancel && customButton,
  });
};

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      className="toaster group"
      richColors
      toastOptions={{
        classNames: {
          toast:
            'group toast group-[.toaster]:bg-surface-secondary group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
          description: '!text-content-secondary',
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast, toastWithCustomButton };
